<template>
    <div class="container">
        <div class="row justify-content-center">
            <div class="col-12 col-xl-10 col-xxl-9">
                <br />
                <br />
                <div class="mb-4">
                    <router-link :to="{ name: 'ChatAgents' }">
                        <span class="icon icon-sm icon-glyphicons-basic-chevron-left mr-1 align-middle"></span>
                        <span class="align-middle">Back to Chat Agents</span>
                    </router-link>
                </div>
                <div>
                    <div class="mb-4 d-flex">
                        <div class="flex-grow-1">
                            <h1 class="h5 mb-2">
                                <span class="icon icon-100 icon-glyphicons-basic-user mr-1 align-middle"></span>
                                <span class="align-middle">{{ chatAgent.name }}</span>
                            </h1>
                            <p class="mb-2 text-secondary">
                                {{ chatAgent.description }}
                            </p>
                            <p class="mb-0 text-secondary small" v-if="chatAgent.last_modified_by">
                                Last modified by {{ chatAgent.last_modified_by.email }} on
                                {{ formatDateTime(chatAgent.last_modified) }}
                            </p>
                        </div>
                        <div class="flex-grow-0 flex-shrink-0 d-flex flex-column align-items-end">
                            <button class="btn btn-gray mb-3" @click="openTestAgentModal()">
                                <span class="icon icon-glyphicons-basic-chat-conversation mr-1"></span>
                                <span>Test Agent</span>
                            </button>
                            <div v-show="agentIsTraining">
                                <div
                                    class="spinner-border spinner-border-tiny text-primary mr-2 align-middle"
                                    role="status"
                                >
                                    <span class="sr-only">Loading...</span>
                                </div>
                                <span class="text-secondary small align-middle">Agent is training...</span>
                            </div>
                        </div>
                    </div>
                    <hr />
                    <div class="mb-4 pt-2">
                        <ul class="nav">
                            <li class="nav-item tab-nav mr-1">
                                <a
                                    class="nav-link tab-nav-link"
                                    :class="{ active: activeTab == 'configure' }"
                                    @click="setActiveTab('configure')"
                                >
                                    <span class="mr-2">Configure</span>
                                    <span class="badge badge-light text-primary align-text-bottom">{{
                                        chatAgent.intents.length
                                    }}</span>
                                </a>
                            </li>
                            <li class="nav-item tab-nav mr-1">
                                <a
                                    class="nav-link tab-nav-link"
                                    :class="{ active: activeTab == 'review' }"
                                    @click="setActiveTab('review')"
                                >
                                    <span class="mr-2">Review</span>
                                    <span
                                        class="badge badge-light align-text-bottom"
                                        :class="{
                                            'text-danger': chatAgent.unreviewed_interaction_count > 0,
                                            'text-success': chatAgent.unreviewed_interaction_count == 0,
                                        }"
                                    >
                                        <span
                                            v-if="chatAgent.unreviewed_interaction_count > 0"
                                            class="icon icon-100 icon-glyphicons-basic-circle-alert mr-xs"
                                        ></span>
                                        <span>{{ chatAgent.unreviewed_interaction_count }}</span>
                                    </span>
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div v-if="activeTab == 'configure'">
                        <div class="d-flex flex-column flex-sm-row align-items-sm-center mb-3">
                            <div class="flex-grow-1 mr-sm-5">
                                <p class="text-secondary mb-2 mb-sm-0">
                                    Create intents to train your chat agent to recognize learner input, and provide a
                                    response. Group questions or statements that share the same meaning into intents.
                                </p>
                            </div>
                            <div class="flex-grow-0 flex-shrink-0">
                                <button @click="openCreateIntent()" class="btn btn-sm btn-primary">
                                    <span class="icon icon-glyphicons-basic-plus mr-1"></span>
                                    <span>New Intent</span>
                                </button>
                            </div>
                        </div>
                        <div class="mb-4 mt-4" v-show="chatAgent.language_codes.length > 1">
                            <div class="d-flex align-items-center">
                                <div class="medium mr-2">
                                    <span>Language:</span>
                                </div>
                                <ul class="nav nav-pills">
                                    <li
                                        class="nav-item"
                                        v-for="languageCode in chatAgent.language_codes"
                                        :key="languageCode"
                                    >
                                        <a
                                            class="nav-link px-2 py-1"
                                            :class="{ active: languageCode == selectedLanguageCode }"
                                            @click="setActiveLanguage(languageCode)"
                                            >{{ languageCode }}</a
                                        >
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <!-- List Display -->
                        <ul class="list-group">
                            <li v-for="intent in chatAgent.intents" :key="intent.name" class="list-group-item">
                                <div class="d-flex flex-column align-items-sm-center flex-sm-row">
                                    <div class="flex-grow-1">
                                        <p class="mb-0">{{ intent.display_name }}</p>
                                    </div>
                                    <div class="flex-shrink-0">
                                        <button
                                            @click="openRemoveIntent(intent)"
                                            type="button"
                                            class="btn btn-light btn-sm mr-1"
                                            :disabled="
                                                ['Default Welcome Intent', 'Default Fallback Intent'].includes(
                                                    intent.display_name
                                                )
                                            "
                                        >
                                            <span class="icon icon-glyphicons-basic-bin"></span>
                                        </button>
                                        <button @click="openEditIntent(intent)" class="btn btn-light btn-sm">
                                            <span class="icon icon-glyphicons-basic-chat-message mr-1"></span>
                                            <span>Phrases & Responses</span>
                                        </button>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <div v-else>
                        <div class="mb-4">
                            <p class="text-secondary mb-0">
                                Review conversations between your learners and this chat agent. Train your chat agent by
                                assigning missed phrases to existing intents.
                            </p>
                        </div>
                        <div class="mb-4">
                            <div class="d-flex">
                                <div class="flex-grow-0">
                                    <select v-model="sessionFilter" class="form-control">
                                        <option value="all">All Recent Conversations</option>
                                        <option value="noMatch">No-Match Conversations Only</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <p class="text-secondary small mb-0 mt-3 pl-1">
                                    Note: Preview and test conversations are excluded from the list below.
                                </p>
                            </div>
                        </div>
                        <div>
                            <ul v-if="reviewSessions.length > 0" class="list-group">
                                <li v-for="session in reviewSessions" :key="session.id" class="list-group-item">
                                    <div
                                        class="d-flex align-items-center pointer"
                                        @click="viewSession(session)"
                                        role="button"
                                        aria-label="Expand conversation"
                                    >
                                        <div class="flex-grow-1">
                                            <p class="mb-1">
                                                <span class="font-italic mr-2">{{
                                                    session.chat_interactions[0].user_input
                                                }}</span>
                                                <span class="badge badge-gray">{{
                                                    session.chat_interactions.length
                                                }}</span>
                                            </p>
                                            <p class="mb-0 text-secondary small">
                                                {{ formatDateTime(session.chat_interactions[0].timestamp) }}
                                            </p>
                                        </div>
                                        <div class="flex-grow-0 mr-2">
                                            <span
                                                v-show="session.unreviewed_interaction_count > 0 && !session.isExpanded"
                                                class="icon icon-glyphicons-basic-circle-alert text-danger mr-1"
                                                ref="helpTextHover"
                                                data-trigger="hover"
                                                data-content="This conversation contains learner inputs that didn't match a configured intent."
                                            ></span>
                                        </div>
                                        <div class="flex-grow-0">
                                            <span
                                                v-show="!session.isExpanded"
                                                class="icon text-secondary icon-glyphicons-basic-chevron-up"
                                            ></span>
                                            <span
                                                v-show="session.isExpanded"
                                                class="icon text-secondary icon-glyphicons-basic-chevron-down"
                                            ></span>
                                        </div>
                                    </div>
                                    <div v-show="session.isExpanded" class="pl-1 pl-md-3 mt-3">
                                        <div
                                            v-for="(interaction, index) in session.chat_interactions"
                                            :key="interaction.id"
                                            class="mb-2"
                                        >
                                            <div class="mb-0">
                                                <div class="d-flex">
                                                    <div class="flex-grow-1 mr-lg-3">
                                                        <div class="d-flex align-items-center">
                                                            <div>
                                                                <span class="badge badge-primary speaker-badge"
                                                                    >Learner</span
                                                                >
                                                            </div>
                                                            <div>
                                                                <div class="pb-0">
                                                                    <span
                                                                        class="text-secondary font-italic mr-2 pr-1"
                                                                        >{{ interaction.user_input }}</span
                                                                    >
                                                                </div>
                                                            </div>
                                                            <div class="d-flex align-items-center">
                                                                <span
                                                                    v-if="
                                                                        !interaction.no_intent_match &&
                                                                        interaction.intent_code_matched
                                                                    "
                                                                    class="text-secondary align-middle mx-2"
                                                                >
                                                                    <span class="font-weight-500">
                                                                        <span class="badge badge-tiny badge-light mb-1">
                                                                            Detected:
                                                                            {{ interaction.intent_code_matched }}</span
                                                                        ></span
                                                                    >
                                                                </span>
                                                                <span
                                                                    v-if="interaction.is_reviewed"
                                                                    class="ml-2 tiny align-middle"
                                                                >
                                                                    <span
                                                                        class="
                                                                            icon icon-glyphicons-basic-circle-check
                                                                            align-middle
                                                                            text-success
                                                                            mr-1
                                                                        "
                                                                    ></span>
                                                                    <span class="text-success align-middle"
                                                                        >Reviewed</span
                                                                    >
                                                                </span>
                                                                <span
                                                                    v-else-if="interaction.needs_review"
                                                                    class="ml-2 tiny"
                                                                >
                                                                    <info-popover
                                                                        content="No matched intent - chat agent used fallback intent."
                                                                        justify="left"
                                                                    >
                                                                        <span
                                                                            class="
                                                                                icon icon-glyphicons-basic-circle-alert
                                                                                text-danger
                                                                                mr-1
                                                                            "
                                                                        ></span>
                                                                        <span class="text-danger">No Match</span>
                                                                    </info-popover>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="flex-grow-0">
                                                        <div
                                                            class="d-flex align-items-center"
                                                            v-if="
                                                                interaction.no_intent_match && !interaction.is_reviewed
                                                            "
                                                        >
                                                            <div class="mr-1">
                                                                <button
                                                                    @click="openAssignModal(interaction)"
                                                                    class="btn btn-link btn-sm px-1 py-0 mr-1"
                                                                >
                                                                    Assign to intent
                                                                </button>
                                                                <button
                                                                    @click="openIgnoreModal(interaction)"
                                                                    class="btn btn-link btn-sm px-1 py-0"
                                                                >
                                                                    Ignore
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div v-else-if="!interaction.is_reviewed">
                                                            <button
                                                                @click="openAssignModal(interaction)"
                                                                class="btn btn-link btn-sm px-1 py-0 mr-1"
                                                            >
                                                                Assign to different intent
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <hr class="my-3" />
                                            <div class="mb-0 d-flex">
                                                <div class="flex-grow-1">
                                                    <span class="badge badge-warning speaker-badge">Agent</span>
                                                    <span class="text-secondary font-italic mr-2">{{
                                                        interaction.avatar_response
                                                    }}</span>
                                                </div>
                                            </div>
                                            <hr class="my-3" v-if="index < session.chat_interactions.length - 1" />
                                        </div>
                                    </div>
                                </li>
                            </ul>
                            <div v-else-if="!loading">
                                <p class="mt-3 text-secondary">No conversations to display.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Edit Intent Modal -->
        <base-modal id="edit-intent-modal" :loading="modalLoading" :centered="true" size="large">
            <template slot="title">
                <span v-if="editingFallbackIntent">Edit Fallback Intent</span>
                <span v-else-if="selectedIntent.name">Edit Intent</span>
                <span v-else>Create New Intent</span>
            </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <div>
                            <base-form
                                :fields="editIntentFields"
                                v-model="selectedIntent"
                                ref="editIntentForm"
                            ></base-form>
                            <p v-show="intentNameConflict" class="small text-danger mb-0">
                                This chat agent already has an intent labelled
                                <strong>{{ selectedIntent.display_name }}</strong> - intent labels must be unique within
                                chat agents.
                            </p>
                        </div>
                        <div v-if="!editingFallbackIntent">
                            <hr />
                            <p class="mb-1">
                                <span class="icon icon-100 icon-glyphicons-basic-chat-message align-middle mr-1"></span>
                                <span class="align-middle mr-2">Training Phrases</span>
                                <span class="align-middle badge badge-gray">{{
                                    selectedIntent.training_phrases.length
                                }}</span>
                            </p>
                            <p class="text-secondary small mb-2">
                                A list of phrase variations to train the chat agent to recognize this intent. This
                                doesn't have to be an exhaustive list of all possible phrases, you just need to add a
                                few.
                            </p>
                            <div class="p-2 rounded bg-light">
                                <div
                                    v-for="(phrase, index) in selectedIntent.training_phrases"
                                    :key="phrase.name"
                                    class="mb-2 mt-2"
                                >
                                    <div class="d-flex flex-colum flex-sm-row align-items-center">
                                        <div class="flex-grow-1 mr-2">
                                            <input
                                                type="text"
                                                placeholder="Training phrase"
                                                class="form-control"
                                                v-model="phrase.text"
                                            />
                                        </div>
                                        <div class="flex-grow-0">
                                            <span
                                                class="icon-btn icon icon-glyphicons-basic-bin"
                                                role="button"
                                                tabindex="0"
                                                @click="removeTrainingPhraseByIndex(index)"
                                            ></span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    class="text-center my-2 text-secondary"
                                    v-show="selectedIntent.training_phrases.length == 0"
                                >
                                    No training phrases added.
                                </div>
                            </div>
                            <div class="mt-2 mb-3">
                                <button @click="addTrainingPhrase()" class="btn btn-sm btn-gray">
                                    + Training Phrase
                                </button>
                            </div>
                        </div>
                        <hr />
                        <p class="mb-1">
                            <span class="icon icon-100 icon-glyphicons-basic-chat-message align-middle mr-1"></span>
                            <span class="align-middle mr-2">Response Variations</span>
                            <span class="align-middle badge badge-gray">{{ selectedIntent.responses.length }}</span>
                        </p>
                        <p class="text-secondary small mb-2">
                            <span v-show="editingFallbackIntent">
                                Control how the chat agent should respond when the learner's input doesn't match any
                                intents.
                            </span>
                            <span v-show="!editingFallbackIntent"
                                >How the chat agent should respond when this intent is matched.</span
                            >
                        </p>
                        <div class="p-2 rounded bg-light">
                            <div v-for="(response, index) in selectedIntent.responses" :key="index" class="mb-2 mt-2">
                                <div class="d-flex flex-colum flex-sm-row align-items-center">
                                    <div class="flex-grow-1 mr-2">
                                        <input
                                            type="text"
                                            placeholder="Response variation"
                                            class="form-control"
                                            v-model="selectedIntent.responses[index]"
                                        />
                                    </div>
                                    <div class="flex-grow-0">
                                        <span
                                            class="icon-btn icon icon-glyphicons-basic-bin"
                                            role="button"
                                            tabindex="0"
                                            @click="removeResponseByIndex(index)"
                                        ></span>
                                    </div>
                                </div>
                            </div>
                            <div class="text-center my-2 text-secondary" v-show="selectedIntent.responses.length == 0">
                                No response variations added.
                            </div>
                        </div>
                        <div class="mt-2 mb-3">
                            <button @click="addResponseVariation()" class="btn btn-sm btn-gray">
                                + Response Variation
                            </button>
                        </div>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="saveIntent()"
                    :disabled="modalLoading || intentNameConflict"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>
        <!-- Remove Intent Modal -->
        <base-modal id="remove-intent-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Remove Intent </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <p class="mb-0">
                            Remove intent <strong>{{ selectedIntent.display_name }}</strong
                            >?
                        </p>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="removeIntent()" :disabled="modalLoading" class="btn btn-danger" type="button">
                    Remove
                </button>
            </template>
        </base-modal>
        <!-- Test Chat Agent Modal -->
        <base-modal id="test-agent-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span class="icon icon-glyphicons-basic-chat-conversation mr-1"></span>
                <span>Test {{ chatAgent.name }}</span>
            </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <div>
                            <p class="text-secondary medium">
                                Have a conversation with your chat agent by typing phrases in the input below.
                            </p>
                        </div>
                        <div class="card">
                            <div class="card-body panel-card-body d-flex flex-column global-theme-text">
                                <div class="row mt-2" style="min-height: 340px">
                                    <div class="col-12">
                                        <div class="alert alert-primary mb-0">{{ testQuerySubmitted }}</div>
                                        <hr class="test-chat-divider" />
                                        <div class="alert alert-secondary">
                                            <div v-show="testAgentThinking">
                                                <div class="d-flex align-items-center chat-dot-container">
                                                    <div class="chat-dot chat-dot-1"></div>
                                                    <div class="chat-dot chat-dot-2"></div>
                                                    <div class="chat-dot chat-dot-3"></div>
                                                </div>
                                            </div>
                                            <div v-show="!testAgentThinking">
                                                {{ testResponse }}
                                            </div>
                                        </div>
                                        <div class="mt-3">
                                            <div v-show="!testAgentThinking && testConversationId">
                                                <div class="d-flex flex-wrap align-items-center">
                                                    <div v-show="testQueryNoMatch" class="flex-grow-0 small mr-2">
                                                        <span
                                                            class="
                                                                icon icon-glyphicons-basic-circle-alert
                                                                text-danger
                                                                mr-1
                                                            "
                                                        ></span>
                                                        <span class="text-secondary">No intent matched.</span>
                                                    </div>
                                                    <div v-show="!testQueryNoMatch" class="flex-grow-0 small mr-2 pr-1">
                                                        <span
                                                            class="
                                                                icon icon-glyphicons-basic-circle-check
                                                                text-success
                                                                mr-1
                                                            "
                                                        ></span>
                                                        <span class="text-secondary"></span>
                                                        <span v-show="testIntentAssigned" class="mr-1"
                                                            >Assigned to intent:</span
                                                        >
                                                        <span v-show="!testIntentAssigned" class="mr-1"
                                                            >Intent matched:</span
                                                        >
                                                        <span class="font-weight-bold">{{ testIntentDetected }}</span>
                                                    </div>
                                                    <div class="flex-grow-0 small" v-show="!testIntentAssigned">
                                                        <div class="d-flex align-items-center">
                                                            <button
                                                                v-show="!displayTestAssignForm"
                                                                @click="showTestAssign()"
                                                                class="btn btn-link btn-sm px-1 py-0 mr-1"
                                                            >
                                                                <span v-show="testQueryNoMatch">Assign to intent</span>
                                                                <span v-show="!testQueryNoMatch"
                                                                    >Assign to a different intent</span
                                                                >
                                                            </button>
                                                            <button
                                                                v-show="displayTestAssignForm"
                                                                @click="closeTestAssign()"
                                                                class="btn btn-link btn-sm px-1 py-0 mr-1"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div v-show="displayTestAssignForm" class="mt-2 pt-1 medium">
                                                    <chat-intent-selector
                                                        v-model="assignToIntent"
                                                        :intent-list="nonFallbackIntents"
                                                        placeholder="Assign to intent..."
                                                    ></chat-intent-selector>
                                                    <div class="d-flex mt-2">
                                                        <div class="flex-grow-1">
                                                            <div v-show="chatAgent.language_codes.length > 1">
                                                                <div class="d-flex align-items-center">
                                                                    <ul class="nav nav-pills">
                                                                        <li
                                                                            class="nav-item"
                                                                            v-for="languageCode in chatAgent.language_codes"
                                                                            :key="languageCode"
                                                                        >
                                                                            <a
                                                                                class="nav-link px-2 py-1"
                                                                                :class="{
                                                                                    active:
                                                                                        languageCode ==
                                                                                        selectedLanguageCode,
                                                                                }"
                                                                                @click="setActiveLanguage(languageCode)"
                                                                                >{{ languageCode }}</a
                                                                            >
                                                                        </li>
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div class="flex-grow-0">
                                                            <div class="d-flex justify-content-end mt-2">
                                                                <button
                                                                    class="btn btn-sm btn-primary"
                                                                    @click="assignPhraseToIntent()"
                                                                    :disabled="!testAssignSubmitEnabled"
                                                                >
                                                                    <div
                                                                        v-show="assignPhraseLoading"
                                                                        class="spinner-border spinner-border-sm mr-1"
                                                                        role="status"
                                                                    >
                                                                        <span class="sr-only">Loading...</span>
                                                                    </div>
                                                                    <span> Assign </span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="card-footer">
                                <div class="d-flex align-items-center" style="margin-top: 6px; margin-bottom: 6px">
                                    <div class="flex-grow-1 mr-1">
                                        <input
                                            type="text"
                                            class="form-control chat-input"
                                            placeholder="Say something to your chat agent here..."
                                            v-model="testQuery"
                                            v-on:keyup.enter="submitTestQuery()"
                                            maxlength="255"
                                        />
                                    </div>
                                    <div class="flex-grow-0 flex-shrink-0">
                                        <span
                                            class="icon icon-glyphicons-basic-circle-up chat-submit-btn align-middle"
                                            @click="submitTestQuery()"
                                        ></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="d-flex align-items-center mt-3">
                            <div class="flex-grow-0 mr-4">
                                <p
                                    class="text-secondary small mb-0"
                                    v-show="selectedLanguageCode && chatAgent.language_codes.length > 1"
                                >
                                    Testing in language code:
                                    <span class="font-weight-bold">{{ selectedLanguageCode }}</span>
                                </p>
                            </div>
                            <div v-show="agentIsTraining">
                                <div
                                    class="spinner-border spinner-border-tiny text-primary mr-2 align-middle"
                                    role="status"
                                >
                                    <span class="sr-only">Loading...</span>
                                </div>
                                <span class="text-secondary small align-middle">Agent is training...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
            </template>
        </base-modal>
        <!-- Assign To Intent Modal -->
        <base-modal id="assign-to-intent-modal" :loading="modalLoading" :centered="true">
            <template slot="title">
                <span v-if="selectedInteraction.no_intent_match">Assign Phrase To Intent</span>
                <span v-else>Assign phrase to a different intent</span></template
            >
            <template>
                <div class="row">
                    <div class="col-12">
                        <p class="small text-secondary mb-3">
                            <span v-if="selectedInteraction.no_intent_match" class="mr-1">Missed learner input:</span>
                            <span v-else class="mr-1">Learner input:</span>
                            <span class="font-weight-bold font-italic">{{ selectedInteraction.user_input }}</span>
                        </p>
                        <p class="mb-3">
                            Assign missed phrase "<span class="font-weight-bold font-italic">{{
                                selectedInteraction.user_input
                            }}</span
                            >" to a new or existing intent.
                        </p>
                        <div class="mb-3 medium" v-show="chatAgent.language_codes.length > 1">
                            <div class="d-flex align-items-center">
                                <div class="medium mr-2">
                                    <span>Language:</span>
                                </div>
                                <ul class="nav nav-pills">
                                    <li
                                        class="nav-item"
                                        v-for="languageCode in chatAgent.language_codes"
                                        :key="languageCode"
                                    >
                                        <a
                                            class="nav-link px-2 py-1"
                                            :class="{ active: languageCode == selectedLanguageCode }"
                                            @click="setActiveLanguage(languageCode)"
                                            >{{ languageCode }}</a
                                        >
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <chat-intent-selector
                            v-model="assignToIntent"
                            :intent-list="nonFallbackIntents"
                        ></chat-intent-selector>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="assignInteractionToIntent()"
                    :disabled="!assignInteractionSubmitEnabled"
                    class="btn btn-primary"
                    type="button"
                >
                    Assign
                </button>
            </template>
        </base-modal>
        <!-- Ignore Intent Modal -->
        <base-modal id="ignore-phrase-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> Ignore Missed Phrase </template>
            <template>
                <div class="row">
                    <div class="col-12">
                        <p class="small text-secondary mb-3">
                            Missed learner input:
                            <span class="font-weight-bold font-italic">{{ selectedInteraction.user_input }}</span>
                        </p>
                        <p class="mb-1">
                            Ignore missed phrase "<span class="font-weight-bold font-italic">{{
                                selectedInteraction.user_input
                            }}</span
                            >"?
                        </p>
                    </div>
                </div>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button @click="ignorePhrase()" :disabled="modalLoading" class="btn btn-primary" type="button">
                    Ignore
                </button>
            </template>
        </base-modal>
    </div>
</template>

<script>
import http from "@/axios.js"
import Utilities from "@/assets/js/utilities.js"
import BaseModal from "@/components/BaseModal"
import BaseForm from "@/components/BaseForm"
import ChatIntentSelector from "@/components/ChatIntentSelector"
import InfoPopover from "@/components/InfoPopover"

export default {
    name: "ChatAgentDetail",
    components: {
        BaseModal,
        BaseForm,
        ChatIntentSelector,
        InfoPopover,
    },
    data() {
        return {
            chatAgent: {
                language_codes: [],
                intents: [],
            },
            modalLoading: false,
            selectedIntent: {
                training_phrases: [],
                responses: [],
            },
            selectedLanguageCode: null,
            activeTab: "configure",
            testAgentThinking: false,
            testQuery: "",
            testQuerySubmitted: "",
            testResponse: "",
            testConversationId: null,
            testIntentDetected: null,
            testQueryNoMatch: null,
            reviewSessions: [],
            selectedSession: {},
            sessionFilter: "all",
            selectedInteraction: {},
            assignToIntent: null,
            displayTestAssignForm: false,
            testIntentAssigned: false,
            assignPhraseLoading: false,
            agentIsTraining: false,
            agentTrainingTimerId: null,
        }
    },
    computed: {
        chatAgentId() {
            return this.$route.params.id
        },
        loading() {
            return this.$store.getters.loading
        },
        intentNameInputDisabled() {
            return ["Default Welcome Intent", "Default Fallback Intent"].includes(this.selectedIntent.display_name)
        },
        editingFallbackIntent() {
            return this.selectedIntent.display_name === "Default Fallback Intent"
        },
        editIntentFields() {
            let vm = this
            return [
                {
                    name: "Label",
                    key: "display_name",
                    subtext: "A label to identify this intent when authoring scenarios.",
                    placeholder: "Intent Label",
                    maxlength: 100,
                    required: true,
                    disabled() {
                        return vm.intentNameInputDisabled
                    },
                },
            ]
        },
        // Returns true if the user is trying to create an intent with the same name as an
        // existing one (the backend doesn't allow duplicates).
        intentNameConflict() {
            if (!this.selectedIntent.display_name) {
                return false
            }
            let vm = this
            let duplicateConflict = this.chatAgent.intents.find((intent) => {
                return (
                    intent.display_name.toLowerCase() == vm.selectedIntent.display_name.toLowerCase() &&
                    vm.selectedIntent.name != intent.name
                )
            })
            return duplicateConflict
        },
        nonFallbackIntents() {
            return this.chatAgent.intents.filter((i) => i.display_name !== "Default Fallback Intent")
        },
        testAssignSubmitEnabled() {
            if (this.assignPhraseLoading) {
                return false
            }
            if (!this.assignToIntent) {
                return false
            }
            if (this.assignToIntent.isNew && !this.assignToIntent.response) {
                return false
            }
            return true
        },
        assignInteractionSubmitEnabled() {
            if (this.modalLoading) {
                return false
            }
            if (!this.assignToIntent) {
                return false
            }
            if (this.assignToIntent.isNew && !this.assignToIntent.response) {
                return false
            }
            return true
        },
    },
    watch: {
        sessionFilter(value, newValue) {
            if (value != newValue) {
                this.fetchSessions()
            }
        },
    },
    methods: {
        fetchAgent(silent = false) {
            if (!silent) {
                this.$store.dispatch("addLoad")
                this.modalLoading = true
            }
            let vm = this
            return http
                .get(`/api/chat-agents/${this.chatAgentId}/`, {
                    params: {
                        languageCode: this.selectedLanguageCode,
                    },
                })
                .then(function (response) {
                    vm.chatAgent = response.data
                    if (!vm.selectedLanguageCode) {
                        vm.selectedLanguageCode = response.data.default_language_code
                    }
                    vm.assignToIntent = null
                })
                .finally(function () {
                    if (!silent) {
                        vm.$store.dispatch("removeLoad")
                        vm.modalLoading = false
                    }
                })
        },
        async refreshAgent(silent = false) {
            let vm = this
            this.fetchAgent(silent).then(function () {
                // This mimics the agent training-in-progress spinner in dialogflow, to let
                // the user know that the agent needs some time to be trained after being edited.
                // To be replaced by an actual poll to the chat agent training status.
                if (vm.agentTrainingTimerId) {
                    window.clearTimeout(vm.agentTrainingTimerId)
                }
                vm.agentIsTraining = true
                vm.agentTrainingTimerId = window.setTimeout(function () {
                    vm.agentIsTraining = false
                }, 16000)
            })
        },
        fetchSessions() {
            this.$store.dispatch("addLoad")
            let vm = this
            let noMatchOnly = this.sessionFilter == "noMatch" ? true : false
            http.get(`/api/chat-agents/${this.chatAgentId}/review/`, {
                params: {
                    noMatchOnly: noMatchOnly,
                },
            })
                .then(function (response) {
                    vm.reviewSessions = response.data
                    // Collapse the currently expanded session.
                    vm.selectedSession.isExpanded = false
                    vm.selectedSession = {}
                })
                .finally(function () {
                    vm.$store.dispatch("removeLoad")
                })
        },
        fetchIntent(intent) {
            this.modalLoading = true
            let vm = this
            http.get(`/api/chat-agents/${this.chatAgentId}/intent/`, {
                params: {
                    name: intent.name,
                    languageCode: this.selectedLanguageCode,
                },
            })
                .then(function (response) {
                    vm.selectedIntent = response.data
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        saveIntent() {
            if (!this.$refs.editIntentForm.validate()) {
                return
            }
            if (this.selectedIntent.name) {
                this.updateIntent()
            } else {
                this.createIntent()
            }
        },
        updateIntent() {
            this.modalLoading = true

            // Filter out empty training phrases and responses.
            let filtered_phrases = this.selectedIntent.training_phrases.filter((p) => p.text !== "")
            let filtered_responses = this.selectedIntent.responses.filter((r) => r !== "")

            let vm = this
            http.put(`/api/chat-agents/${this.chatAgentId}/intent/`, {
                name: this.selectedIntent.name,
                display_name: this.selectedIntent.display_name,
                training_phrases: filtered_phrases,
                responses: filtered_responses,
                languageCode: this.selectedLanguageCode,
            })
                .then(function () {
                    $("#edit-intent-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Intent updated.",
                        data: {},
                    })
                    vm.refreshAgent()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        createIntent() {
            if (this.intentNameConflict) {
                return
            }
            this.modalLoading = true

            // Filter out empty training phrases and responses.
            let filtered_phrases = this.selectedIntent.training_phrases.filter((p) => p.text !== "").map((p) => p.text)
            let filtered_responses = this.selectedIntent.responses.filter((r) => r !== "")

            let vm = this
            http.post(`/api/chat-agents/${this.chatAgent.id}/intents/`, {
                display_name: vm.selectedIntent.display_name,
                languageCode: this.selectedLanguageCode,
                training_phrases: filtered_phrases,
                responses: filtered_responses,
            })
                .then(function () {
                    $("#edit-intent-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Intent created.",
                        data: {},
                    })
                    vm.refreshAgent()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        removeIntent() {
            this.modalLoading = true
            let vm = this
            http.put(`/api/chat-agents/${this.chatAgent.id}/intent/remove/`, {
                name: this.selectedIntent.name,
            })
                .then(function () {
                    $("#remove-intent-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Intent removed.",
                        data: {},
                    })
                    vm.refreshAgent()
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        openEditIntent(intent) {
            this.$refs.editIntentForm.resetValidate()
            this.selectedIntent = {
                name: intent.name,
                display_name: intent.display_name,
                training_phrases: [],
                responses: [],
            }
            $("#edit-intent-modal").modal("show")
            this.fetchIntent(intent)
        },
        openRemoveIntent(intent) {
            this.selectedIntent = {
                name: intent.name,
                display_name: intent.display_name,
                training_phrases: [],
                responses: [],
            }
            $("#remove-intent-modal").modal("show")
        },
        openCreateIntent() {
            this.$refs.editIntentForm.resetValidate()
            this.selectedIntent = {
                training_phrases: [],
                responses: [],
                name: "",
            }
            $("#edit-intent-modal").modal("show")
        },
        addTrainingPhrase() {
            this.selectedIntent.training_phrases.push({
                text: "",
            })
        },
        addResponseVariation() {
            this.selectedIntent.responses.push("")
        },
        removeTrainingPhraseByIndex(index) {
            this.selectedIntent.training_phrases.splice(index, 1)
        },
        removeResponseByIndex(index) {
            this.selectedIntent.responses.splice(index, 1)
        },
        setActiveLanguage(languageCode) {
            this.selectedLanguageCode = languageCode
            this.fetchAgent()
        },
        // Format a date object as a string.
        formatDateTime(dateObj) {
            return Utilities.formatDateTime(dateObj)
        },
        setActiveTab(tabName) {
            this.activeTab = tabName
            this.$router.push({
                path: this.$route.path,
                query: {
                    tab: tabName,
                },
                params: {
                    savePosition: true,
                },
            })
        },
        openTestAgentModal() {
            $("#test-agent-modal").modal("show")
            this.testQuery = ""
            this.testResponse = "The chat agent will respond here."
            this.testQuerySubmitted = "Your test inputs will appear here."
            this.testConversationId = null
            this.displayTestAssignForm = false
            this.testIntentAssigned = false
        },
        submitTestQuery() {
            if (!this.testQuery) {
                return
            }
            this.displayTestAssignForm = false
            this.testAgentThinking = true
            this.testQuerySubmitted = this.testQuery
            this.testIntentAssigned = false
            this.testQuery = ""
            let vm = this
            http.post(`/api/chat-agents/${this.chatAgentId}/test/`, {
                query: this.testQuerySubmitted,
                conversationId: this.testConversationId,
                languageCode: this.selectedLanguageCode,
            })
                .then(function (response) {
                    vm.testResponse = response.data.agent_response
                    vm.testIntentDetected = response.data.intent_detected
                    vm.testQueryNoMatch = response.data.no_match
                    vm.testConversationId = response.data.conversation_id
                })
                .finally(() => {
                    vm.testAgentThinking = false
                })
        },
        viewSession(session) {
            if (this.selectedSession.id == session.id) {
                // User has selected the same session, so just toggle its state.
                this.selectedSession.isExpanded = !this.selectedSession.isExpanded
                return
            }
            // Collapse the currently expanded session.
            this.selectedSession.isExpanded = false
            this.$set(session, "isExpanded", true)
            this.selectedSession = session
        },
        openAssignModal(interaction) {
            $("#assign-to-intent-modal").modal("show")
            this.selectedInteraction = interaction
            this.assignToIntent = null
        },
        showTestAssign() {
            this.assignToIntent = null
            this.displayTestAssignForm = true
        },
        closeTestAssign() {
            this.displayTestAssignForm = false
        },
        openIgnoreModal(interaction) {
            $("#ignore-phrase-modal").modal("show")
            this.selectedInteraction = interaction
        },
        assignPhraseToIntent() {
            let vm = this
            this.assignPhraseLoading = true
            http.post(`/api/chat-agents/${this.chatAgentId}/intent/add-phrase/`, {
                languageCode: this.selectedLanguageCode,
                intentName: this.assignToIntent.name,
                intentDisplayName: this.assignToIntent.display_name,
                intentTrainingPhrase: this.testQuerySubmitted,
                intentResponse: this.assignToIntent.response,
            })
                .then(function () {
                    vm.testQueryNoMatch = false
                    vm.testIntentDetected = vm.assignToIntent.display_name
                    vm.displayTestAssignForm = false
                    vm.testIntentAssigned = true
                    vm.refreshAgent(true)
                })
                .finally(function () {
                    vm.assignPhraseLoading = false
                })
        },
        assignInteractionToIntent() {
            if (!this.assignToIntent) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.post(`/api/interactions/${this.selectedInteraction.id}/add-to-intent/`, {
                languageCode: this.selectedLanguageCode,
                intentName: this.assignToIntent.name,
                intentDisplayName: this.assignToIntent.display_name,
                intentResponse: this.assignToIntent.response,
            })
                .then(function () {
                    vm.$set(vm.selectedInteraction, "is_reviewed", true)
                    vm.$set(vm.selectedInteraction, "needs_review", false)
                    vm.recalculateUnreviewedInteractions()
                    vm.refreshBasicAgentDetails()
                    vm.refreshAgent(true)
                    $("#assign-to-intent-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Phrase added to intent.",
                        data: {},
                    })
                })
                .finally(() => {
                    vm.modalLoading = false
                })
        },
        ignorePhrase() {
            this.modalLoading = true
            let vm = this
            http.post(`/api/interactions/${this.selectedInteraction.id}/ignore/`)
                .then(function () {
                    vm.$set(vm.selectedInteraction, "is_reviewed", true)
                    vm.$set(vm.selectedInteraction, "needs_review", false)
                    vm.recalculateUnreviewedInteractions()
                    vm.refreshBasicAgentDetails()
                    $("#ignore-phrase-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Phrase ignored.",
                        data: {},
                    })
                })
                .finally(() => {
                    vm.modalLoading = false
                })
        },
        refreshBasicAgentDetails() {
            let vm = this
            http.get(`/api/chat-agents/${this.chatAgent.id}/basic/`).then(function (response) {
                vm.chatAgent.unreviewed_interaction_count = response.data.unreviewed_interaction_count
                vm.chatAgent.last_modified = response.data.last_modified
                vm.chatAgent.last_modified_by = response.data.last_modified_by
            })
        },
        recalculateUnreviewedInteractions() {
            // Re-calculate the number of unreviewed no-match interactions for the currently
            // selected session.
            let unreviewedInteractionsCount = this.selectedSession.chat_interactions.filter(
                (i) => i.no_intent_match && !i.is_reviewed
            ).length
            this.$set(this.selectedSession, "unreviewed_interaction_count", unreviewedInteractionsCount)
        },
    },
    mounted() {
        this.fetchAgent()
        this.fetchSessions()
        if (this.$route.query.tab) {
            this.activeTab = this.$route.query.tab
        }
    },
    beforeDestroy() {
        if (this.agentTrainingTimerId) {
            window.clearTimeout(this.agentTrainingTimerId)
        }
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/chat.scss";

.speaker-badge {
    min-width: 56px;
    margin-right: 14px;
}

.test-chat-divider {
    margin-top: 12px;
    margin-bottom: 12px;
}

.matched-code {
    font-size: 0.7rem;
}
</style>